// src/shared/utils/apiResponseUtils.ts

/**
 * Safely extracts an array of results from an Orval/Axios response.
 * Handles the following variations:
 * - { data: { results: [...] } } (Axios wrapped paginated)
 * - { results: [...] } (Unwrapped paginated)
 * - { data: [...] } (Axios wrapped list)
 * - [...] (Raw array)
 * 
 * @param response The raw response from the API hook
 * @returns A guaranteed array of type T
 */
export function extractResults<T>(response: any): T[] {
  if (!response) return [];
  
  const raw = response.results ?? response.data?.results ?? response.data ?? response;
  
  return Array.isArray(raw) ? raw : [];
}

/**
 * Safely extracts a single object from an Orval/Axios response.
 * Handles:
 * - { data: { id: "..." } } (Axios wrapped)
 * - { id: "..." } (Unwrapped)
 * 
 * @param response The raw response from the API hook
 * @returns The extracted data object or null
 */
export function extractData<T>(response: any): T | null {
  if (!response) return null;
  
  if (response.data !== undefined) {
    return response.data as T;
  }
  
  return response as T;
}
